import React from 'react';
import { DropzoneInputProps, DropzoneRootProps } from 'react-dropzone';
import { Control, FieldErrors, UseFormGetValues } from 'react-hook-form';
import { useTheme } from 'styled-components';

import { Input, Splash } from '../../../../../shared/Components';
import { AbsoluteDiv, Box, FlexContainer, Image, Text } from '../../../../../shared/Typhography';
import { strings } from '../../../../../strings';
import { AccountInfoField } from '../../../../../validations/accountInfo';
import { AccountInfoInputs } from '../AccountInfoScreen';

interface Props {
  type?: 'image' | 'password';
  name: string;
  control: Control<any>;
  value: string;
  handleEdit?: () => void;
  getRootProps?: DropzoneRootProps;
  getInputProps?: DropzoneInputProps;
  image?: any;
  setImage: React.Dispatch<React.SetStateAction<boolean | undefined>>;
  loading: boolean;
  errors: FieldErrors;
  getValues: UseFormGetValues<AccountInfoInputs>;
}

const SettingsOption = ({
  name,
  control,
  value,
  type,
  getRootProps,
  getInputProps,
  image,
  setImage,
  handleEdit,
  loading,
  getValues,
  errors,
}: Props) => {
  const { colors } = useTheme();
  const [editing, setEditing] = React.useState<boolean>(false);

  const edit = () => {
    //Logic for if user dont change value in input it will just set editing on false without calling any function.
    if (value == (getValues(AccountInfoField.USERNAME) || getValues(AccountInfoField.NAME))) {
      setEditing((v) => !v);
    } else if ((editing || type === 'image' || type === 'password') && handleEdit) {
      handleEdit();
      setEditing(false);
    } else {
      setEditing(true);
    }
  };

  /**
   *
   * @returns Edit buttons for different options
   */
  const renderEditButton = () => {
    if (type === 'image' && image) {
      return (
        <FlexContainer alignItems="center" gap="1rem">
          <Text
            cursor="pointer"
            weight="bold"
            color={colors.gray}
            onClick={() => setImage && setImage(undefined)}
          >
            {strings.cancel}
          </Text>
          <Text as="div" onClick={edit} cursor="pointer" weight="bold" color={colors.purple}>
            {loading ? <Splash color={colors.purple} width={25} height={25} /> : strings.save}
          </Text>
        </FlexContainer>
      );
    } else if (type === 'image' && !image) {
      return (
        <Text cursor="pointer" weight="bold" color={colors.purple} {...getRootProps}>
          <input {...getInputProps} />
          {strings.change}
        </Text>
      );
    } else {
      return editing ? (
        <FlexContainer gap="1rem">
          <Text
            cursor="pointer"
            weight="bold"
            color={colors.gray}
            onClick={() => setEditing(false)}
          >
            {strings.cancel}
          </Text>
          <Text cursor="pointer" weight="bold" color={colors.purple} onClick={() => edit()}>
            {strings.save}
          </Text>
        </FlexContainer>
      ) : (
        <Text cursor="pointer" weight="bold" color={colors.purple} onClick={() => edit()}>
          {strings.change}
        </Text>
      );
    }
  };

  /**
   *
   * @returns Text with value of field or Image, it depends its option for image change or some input.
   */
  const renderInputOrImage = () => {
    if (type === 'image') {
      return (
        <Image
          size={type === 'image' ? 'profileImage' : 'coverImage'}
          borderRadiusType={type === 'image' ? 'circle' : 'rounded'}
          src={value}
        />
      );
    } else {
      return (
        <FlexContainer
          width="40%"
          height="50px"
          style={{ position: 'relative' }}
          alignItems="center"
        >
          {editing ? (
            <AbsoluteDiv left={-18}>
              <Input
                control={control}
                placeholder={value}
                name={name}
                defaultValue={value}
                error={errors[name]}
              />
            </AbsoluteDiv>
          ) : (
            <Text cursor="pointer" weight="bold" color={colors.gray}>
              {value}
            </Text>
          )}
        </FlexContainer>
      );
    }
  };

  return (
    <Box width="100%">
      <FlexContainer
        height="30px"
        alignContent="center"
        alignItems="center"
        justifyContent="space-between"
      >
        <Text weight="bold">{name}</Text>
        <FlexContainer alignItems="center" justifyContent="space-between" width="40%">
          {renderInputOrImage()}
          {renderEditButton()}
        </FlexContainer>
      </FlexContainer>
    </Box>
  );
};

export default SettingsOption;
